import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Trash2 } from "lucide-react";
import { useState, type FormEvent } from "react";
import {
  describeRecurrence,
  recurrenceProblems,
  type RecurrenceKind,
  type RecurringTask,
  type TaskPriority,
  type WriteRejection,
} from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { can, usePermissions } from "@/auth/permissions";
import { FeedbackButton } from "@/components/feedback/FeedbackButton";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import {
  useProjectDirectory,
  useUserDirectory,
} from "@/modules/tasks/directories";
import {
  isTaskPriority,
  TASK_PRIORITY_OPTIONS,
} from "@/modules/tasks/task-enums";
import { describeRejection } from "@/modules/tasks/writes";

const WEEKDAYS: { value: number; short: string; long: string }[] = [
  { value: 1, short: "Mon", long: "Monday" },
  { value: 2, short: "Tue", long: "Tuesday" },
  { value: 3, short: "Wed", long: "Wednesday" },
  { value: 4, short: "Thu", long: "Thursday" },
  { value: 5, short: "Fri", long: "Friday" },
  { value: 6, short: "Sat", long: "Saturday" },
  { value: 7, short: "Sun", long: "Sunday" },
];

const KINDS: { value: RecurrenceKind; label: string }[] = [
  { value: "daily", label: "Every day" },
  { value: "weekly", label: "Every week" },
  { value: "monthly", label: "Every month" },
];

interface FormState {
  title: string;
  description: string;
  projectId: string;
  priority: TaskPriority;
  assigneeUserId: string;
  kind: RecurrenceKind;
  weekdays: number[];
  dayOfMonth: string;
  timeOfDay: string;
  dueAfterDays: string;
  isActive: boolean;
}

function initialState(recurring?: RecurringTask): FormState {
  return {
    title: recurring?.title ?? "",
    description: recurring?.description ?? "",
    projectId: recurring?.projectId ?? "",
    priority: recurring?.priority ?? "medium",
    assigneeUserId: recurring?.assigneeUserId ?? "",
    kind: recurring?.kind ?? "weekly",
    weekdays: recurring?.weekdays ?? [6],
    dayOfMonth: String(recurring?.dayOfMonth ?? 1),
    timeOfDay: recurring?.timeOfDay ?? "10:00",
    dueAfterDays: String(recurring?.dueAfterDays ?? 0),
    isActive: recurring?.isActive ?? true,
  };
}

type RecurringFormProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
} & ({ mode: "create" } | { mode: "edit"; recurring: RecurringTask });

/**
 * One form for "New recurring task" and "Edit recurring task" (#624): what each
 * task made will say, who it is for, and when it comes round. Pausing is the
 * "Active" box; deleting leaves the tasks already made.
 */
export function RecurringForm(props: RecurringFormProps) {
  const { open, onOpenChange } = props;
  const data = useDataSource();
  const queryClient = useQueryClient();
  const permissions = usePermissions();
  const usersQuery = useUserDirectory();
  const projectsQuery = useProjectDirectory();

  const [state, setState] = useState<FormState>(() =>
    initialState(props.mode === "edit" ? props.recurring : undefined),
  );
  const [rejection, setRejection] = useState<WriteRejection | null>(null);

  function reset() {
    setState(initialState(props.mode === "edit" ? props.recurring : undefined));
    setRejection(null);
  }

  function input() {
    return {
      projectId: state.projectId,
      title: state.title.trim(),
      description: state.description.trim() || undefined,
      priority: state.priority,
      assigneeUserId: state.assigneeUserId,
      kind: state.kind,
      weekdays: state.kind === "weekly" ? state.weekdays : [],
      dayOfMonth: state.kind === "monthly" ? Number(state.dayOfMonth) : null,
      timeOfDay: state.timeOfDay,
      dueAfterDays: Number(state.dueAfterDays),
      isActive: state.isActive,
    };
  }

  const saveMutation = useMutation({
    mutationFn: () =>
      props.mode === "create"
        ? data.tasks.createRecurring(input())
        : data.tasks.updateRecurring(props.recurring.id, input()),
    onSuccess: (result) => {
      if (!result.ok) {
        setRejection(result.rejection);
        return;
      }
      onOpenChange(false);
      void queryClient.invalidateQueries({ queryKey: ["tasks", "recurring"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => {
      if (props.mode !== "edit") throw new Error("nothing to delete yet");
      return data.tasks.deleteRecurring(props.recurring.id);
    },
    onSuccess: (result) => {
      if (!result.ok) {
        setRejection(result.rejection);
        return;
      }
      onOpenChange(false);
      void queryClient.invalidateQueries({ queryKey: ["tasks", "recurring"] });
    },
  });

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    // The same words the server uses, so a bad time never makes the trip.
    const problems = recurrenceProblems(input());
    if (Object.keys(problems).length > 0) {
      setRejection({
        kind: "invalid",
        message: "Check the highlighted fields.",
        fields: problems,
      });
      return;
    }
    setRejection(null);
    saveMutation.mutate();
  }

  const fieldError = (field: string): string | undefined =>
    rejection?.kind === "invalid" ? rejection.fields?.[field] : undefined;

  const pending = saveMutation.isPending || deleteMutation.isPending;
  const users = usersQuery.data?.items ?? [];
  const projects = (projectsQuery.data?.items ?? []).filter(
    (project) =>
      !project.isArchived ||
      (props.mode === "edit" && project.id === props.recurring.projectId),
  );
  const canDelete =
    props.mode === "edit" && can(permissions, "tasks.task.delete");
  const previewable =
    Object.keys(recurrenceProblems(input())).length === 0
      ? describeRecurrence(input())
      : null;

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <SheetContent
        title={
          props.mode === "create" ? "New recurring task" : "Edit recurring task"
        }
        description="A new task is made each time it comes due, and the person it is for is told then."
        actions={<FeedbackButton />}
        className="w-full sm:w-[520px] sm:max-w-[92vw]"
      >
        <form className="flex h-full flex-col" onSubmit={handleSubmit}>
          <div className="flex-1 space-y-4 overflow-auto p-4 sm:p-6">
            <div className="space-y-1.5">
              <Label htmlFor="recurring-title">Title</Label>
              <Input
                id="recurring-title"
                value={state.title}
                placeholder="e.g. Saturday stock count"
                onChange={(event) => {
                  const title = event.target.value;
                  setState((prev) => ({ ...prev, title }));
                }}
                required
              />
              {fieldError("title") ? (
                <p className="text-destructive text-xs">
                  {fieldError("title")}
                </p>
              ) : null}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="recurring-description">
                Description (copied onto every task)
              </Label>
              <Textarea
                id="recurring-description"
                value={state.description}
                onChange={(event) => {
                  const description = event.target.value;
                  setState((prev) => ({ ...prev, description }));
                }}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="recurring-project">Project</Label>
                <Select
                  value={state.projectId}
                  onValueChange={(projectId) => {
                    setState((prev) => ({ ...prev, projectId }));
                  }}
                >
                  <SelectTrigger id="recurring-project" className="w-full">
                    <SelectValue placeholder="Choose a project" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map((project) => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.key} · {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {fieldError("projectId") ? (
                  <p className="text-destructive text-xs">
                    {fieldError("projectId")}
                  </p>
                ) : null}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="recurring-assignee">Who it is for</Label>
                <Select
                  value={state.assigneeUserId}
                  onValueChange={(assigneeUserId) => {
                    setState((prev) => ({ ...prev, assigneeUserId }));
                  }}
                >
                  <SelectTrigger id="recurring-assignee" className="w-full">
                    <SelectValue placeholder="Choose a person" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {fieldError("assigneeUserId") ? (
                  <p className="text-destructive text-xs">
                    {fieldError("assigneeUserId")}
                  </p>
                ) : null}
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="recurring-kind">Repeats</Label>
                <Select
                  value={state.kind}
                  onValueChange={(kind) => {
                    setState((prev) => ({
                      ...prev,
                      kind: kind as RecurrenceKind,
                    }));
                  }}
                >
                  <SelectTrigger id="recurring-kind" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {KINDS.map((kind) => (
                      <SelectItem key={kind.value} value={kind.value}>
                        {kind.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="recurring-time">At</Label>
                <Input
                  id="recurring-time"
                  type="time"
                  value={state.timeOfDay}
                  onChange={(event) => {
                    const timeOfDay = event.target.value;
                    setState((prev) => ({ ...prev, timeOfDay }));
                  }}
                />
                {fieldError("timeOfDay") ? (
                  <p className="text-destructive text-xs">
                    {fieldError("timeOfDay")}
                  </p>
                ) : null}
              </div>
            </div>

            {state.kind === "weekly" ? (
              <div className="space-y-1.5">
                <Label id="recurring-weekdays-label">On</Label>
                <div
                  role="group"
                  aria-labelledby="recurring-weekdays-label"
                  className="flex flex-wrap gap-1.5"
                >
                  {WEEKDAYS.map((day) => {
                    const chosen = state.weekdays.includes(day.value);
                    return (
                      <Button
                        key={day.value}
                        type="button"
                        variant={chosen ? "default" : "outline"}
                        aria-pressed={chosen}
                        aria-label={day.long}
                        className={cn("h-11 min-w-12 sm:h-9")}
                        onClick={() => {
                          setState((prev) => ({
                            ...prev,
                            weekdays: chosen
                              ? prev.weekdays.filter((d) => d !== day.value)
                              : [...prev.weekdays, day.value],
                          }));
                        }}
                      >
                        {day.short}
                      </Button>
                    );
                  })}
                </div>
                {fieldError("weekdays") ? (
                  <p className="text-destructive text-xs">
                    {fieldError("weekdays")}
                  </p>
                ) : null}
              </div>
            ) : null}

            {state.kind === "monthly" ? (
              <div className="space-y-1.5">
                <Label htmlFor="recurring-day">On day of the month</Label>
                <Input
                  id="recurring-day"
                  type="number"
                  min={1}
                  max={31}
                  inputMode="numeric"
                  value={state.dayOfMonth}
                  onChange={(event) => {
                    const dayOfMonth = event.target.value;
                    setState((prev) => ({ ...prev, dayOfMonth }));
                  }}
                />
                <p className="text-muted-foreground text-xs">
                  31 means the last day, in a month that is shorter.
                </p>
                {fieldError("dayOfMonth") ? (
                  <p className="text-destructive text-xs">
                    {fieldError("dayOfMonth")}
                  </p>
                ) : null}
              </div>
            ) : null}

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="recurring-priority">Priority</Label>
                <Select
                  value={state.priority}
                  onValueChange={(priority) => {
                    if (isTaskPriority(priority)) {
                      setState((prev) => ({ ...prev, priority }));
                    }
                  }}
                >
                  <SelectTrigger id="recurring-priority" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TASK_PRIORITY_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="recurring-due">
                  Due, days after (0 = same day)
                </Label>
                <Input
                  id="recurring-due"
                  type="number"
                  min={0}
                  max={30}
                  inputMode="numeric"
                  value={state.dueAfterDays}
                  onChange={(event) => {
                    const dueAfterDays = event.target.value;
                    setState((prev) => ({ ...prev, dueAfterDays }));
                  }}
                />
                {fieldError("dueAfterDays") ? (
                  <p className="text-destructive text-xs">
                    {fieldError("dueAfterDays")}
                  </p>
                ) : null}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="recurring-active"
                checked={state.isActive}
                onCheckedChange={(checked) => {
                  setState((prev) => ({ ...prev, isActive: checked === true }));
                }}
              />
              <Label htmlFor="recurring-active">
                Active (untick to pause; nothing is made while paused)
              </Label>
            </div>

            {previewable ? (
              <p className="bg-muted/40 rounded-md border p-3 text-sm">
                {previewable}
                {state.dueAfterDays === "0" || state.dueAfterDays === ""
                  ? ", due the same day."
                  : `, due ${state.dueAfterDays} day(s) later.`}
              </p>
            ) : null}

            {props.mode === "edit" && props.recurring.lastProblem ? (
              <p className="text-destructive text-sm">
                Last time: {props.recurring.lastProblem}
              </p>
            ) : null}

            {rejection && rejection.kind !== "invalid" ? (
              <p role="alert" className="text-destructive text-sm">
                {describeRejection(rejection)}
              </p>
            ) : null}
          </div>

          <div className="flex shrink-0 items-center justify-between gap-2 border-t p-4 sm:px-6">
            {canDelete ? (
              <Button
                type="button"
                variant="outline"
                className="h-11 sm:h-9"
                disabled={pending}
                onClick={() => {
                  if (
                    window.confirm(
                      "Delete this recurring task? The tasks it already made stay.",
                    )
                  ) {
                    setRejection(null);
                    deleteMutation.mutate();
                  }
                }}
              >
                {deleteMutation.isPending ? (
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                ) : (
                  <Trash2 />
                )}
                Delete
              </Button>
            ) : (
              <span />
            )}
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                className="h-11 sm:h-9"
                onClick={() => {
                  reset();
                  onOpenChange(false);
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="h-11 sm:h-9"
                disabled={
                  pending ||
                  state.title.trim() === "" ||
                  state.projectId === "" ||
                  state.assigneeUserId === ""
                }
              >
                {saveMutation.isPending ? (
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                ) : null}
                {props.mode === "create" ? "Create" : "Save changes"}
              </Button>
            </div>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
